import React, { Component } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import NavBar from "../../components/navbar";
import { Link } from "react-router-dom";

import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  upsertUseCase,
} from "../../usecases/object";
import { exportCSVUseCase } from "../../usecases/csv/usecases";
import {
  addSchemaUseCase,
  deleteSchemaUseCase,
  updateSchemaUseCase,
} from "../../usecases/schema/usecases";
import RecruiterCalendarPresenter from "./RecruiterCalendarPresenter";
import BaseListPage from "../../base/BaseListPage";
import { enIE } from "date-fns/locale";
import withRouter from "../../withRouter";

// Initialize moment localizer
moment.suppressDeprecationWarnings = true;
const localizer = momentLocalizer(moment);

class RecruiterCalendar extends BaseListPage {
  constructor(props) {
    super(props);
    this.presenter = new RecruiterCalendarPresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase(),
      exportCSVUseCase(),
      addSchemaUseCase(),
      updateSchemaUseCase(),
      deleteSchemaUseCase()
    );

    this.state = {
      events: this.props.initialEvents || [],
      view: "month",
      recruiterId: null,
      recruiterUsername: "Loading...",
    };
  }

  getCollectionName = () => "recruiter_calendar";
  getKeys = () => [];
  setTotal = (total) => console.log("Total events:", total);
  setObjects = (objects) => {
    console.log("Set objects:", objects);
    const formattedEvents = this.processEventsArray(objects);
    this.setState({ events: formattedEvents });
  };
  showError = (error) => console.error("Error:", error);

  debugEvents = () => {
    if (!Array.isArray(this.state.events) || this.state.events.length === 0) {
      console.log("No events to debug");
      return;
    }

    console.group("Calendar Events Debug");
    this.state.events.forEach((event, index) => {
      console.log(`Event ${index + 1}: ${event.title}`);
      console.log(`  Start: ${event.start.toLocaleString()}`);
      console.log(`  End: ${event.end.toLocaleString()}`);
      console.log(
        `  Applicant Name: ${
          event.originalEvent.applicantName || "Not specified"
        }`
      );
      console.log(
        `  Applicant Form ID: ${
          event.originalEvent.applicantFormId || "Not specified"
        }`
      );
      const isStartValid = !isNaN(event.start.getTime());
      const isEndValid = !isNaN(event.end.getTime());
      console.log(`  Valid dates? Start: ${isStartValid}, End: ${isEndValid}`);
      console.log(
        `  Start time: ${event.start.getHours()}:${String(
          event.start.getMinutes()
        ).padStart(2, "0")}`
      );
      console.log(
        `  End time: ${event.end.getHours()}:${String(
          event.end.getMinutes()
        ).padStart(2, "0")}`
      );
    });
    console.groupEnd();
  };

  fetchRecruiterUsername = async (recruiterId) => {
    try {
      const userCollection = "users";
      const query = { where: { id: recruiterId } };
      const users = await this.presenter.findObjectUseCase.execute(
        userCollection,
        query
      );
      if (users && users.length > 0) {
        const username =
          users[0].username || users[0].name || users[0].fullName;
        this.setState({ recruiterUsername: username });
      } else {
        this.setState({ recruiterUsername: "Current Recruiter" });
      }
    } catch (error) {
      console.error("Error fetching recruiter username:", error);
      this.setState({ recruiterUsername: "Current Recruiter" });
    }
  };

  componentDidMount() {
    const pathParts = window.location.pathname.split("/");
    const recruiterId = pathParts[pathParts.length - 1];

    console.log("URL path parts:", pathParts);
    console.log("Extracted recruiter ID from URL:", recruiterId);

    const validRecruiterId =
      recruiterId && recruiterId !== "calendar" ? recruiterId : null;

    if (validRecruiterId) {
      console.log("Using recruiter ID from URL:", validRecruiterId);
      this.setState({ recruiterId: validRecruiterId }, () => {
        this.fetchEvents();
        this.fetchRecruiterUsername(validRecruiterId);
      });
    } else {
      const currentUser = this.getCurrentUser();
      const currentRecruiterId = currentUser && currentUser.id;

      console.log(
        "No ID in URL, falling back to current user ID:",
        currentRecruiterId
      );

      if (currentRecruiterId) {
        this.setState({ recruiterId: currentRecruiterId }, () => {
          this.fetchEvents();
          this.fetchRecruiterUsername(currentRecruiterId);
        });
      } else {
        console.error("No recruiter ID found in URL or current user");
      }
    }

    this.pollingInterval = setInterval(() => {
      this.silentRefresh();
    }, 2000);

    const style = document.createElement("style");
    style.innerHTML = `
    .rbc-month-view{
      background-color: #f8f8f8 !important;
    }

    .rbc-time-view {
      background-color: #f8f8f8 !important;
    }

    .rbc-day-view .rbc-time-view,
    .rbc-work-week-view .rbc-time-view {
      background-color: #f8f8f8 !important;
    }

    .rbc-agenda-view {
      background-color: #f8f8f8 !important;
    }

    .rbc-toolbar button {
      background-color: #f8f8f8 !important;
    }
    `;
    document.head.appendChild(style);
  }

  componentWillUnmount() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  silentRefresh = async () => {
    try {
      const { recruiterId } = this.state;
      if (!recruiterId) {
        console.error("No recruiter ID available for silent refresh");
        return;
      }

      const recruiterObject = { id: recruiterId };

      const eventsData = await this.presenter.getRecruiterEvents(
        recruiterObject
      );

      let formattedEvents = [];
      if (Array.isArray(eventsData)) {
        formattedEvents = this.processEventsArray(eventsData);
      } else if (typeof eventsData === "object") {
        formattedEvents = this.processEventsObject(eventsData);
      }

      if (this.eventsHaveChanged(this.state.events, formattedEvents)) {
        console.log(
          "Calendar events updated through silent refresh for recruiter:",
          recruiterId
        );
        this.setState({ events: formattedEvents });
      }
    } catch (error) {
      console.error("Silent refresh error:", error);
    }
  };

  eventsHaveChanged = (currentEvents, newEvents) => {
    if (currentEvents.length !== newEvents.length) {
      return true;
    }

    const currentEventsMap = new Map();
    const newEventsMap = new Map();

    currentEvents.forEach((event) => {
      currentEventsMap.set(event.id, event);
    });

    newEvents.forEach((event) => {
      newEventsMap.set(event.id, event);
    });

    for (const eventId of currentEventsMap.keys()) {
      if (!newEventsMap.has(eventId)) {
        return true;
      }
    }

    for (const eventId of newEventsMap.keys()) {
      if (!currentEventsMap.has(eventId)) {
        return true;
      }

      const currentEvent = currentEventsMap.get(eventId);
      const newEvent = newEventsMap.get(eventId);

      if (
        currentEvent.title !== newEvent.title ||
        currentEvent.start.getTime() !== newEvent.start.getTime() ||
        currentEvent.end.getTime() !== newEvent.end.getTime() ||
        currentEvent.allDay !== newEvent.allDay
      ) {
        return true;
      }
    }

    return false;
  };

  createEventObject = (event) => {
    try {
      if (!event.startDate || !event.endDate) {
        console.error("Missing event date fields:", event);
        return null;
      }

      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error("Invalid date format for event:", event);
        return null;
      }

      let startDateTime, endDateTime;
      let allDay = false;

      if (event.startTime) {
        const timeFormat = event.startTime.includes(":")
          ? event.startTime
          : `${event.startTime}:00`;
        const [startHours, startMinutes] = timeFormat
          .split(":")
          .map((num) => parseInt(num, 10));
        startDateTime = new Date(startDate);
        startDateTime.setHours(startHours, startMinutes, 0, 0);
      } else {
        startDateTime = new Date(startDate);
        startDateTime.setHours(0, 0, 0, 0);
        allDay = true;
      }

      if (event.endTime) {
        const timeFormat = event.endTime.includes(":")
          ? event.endTime
          : `${event.endTime}:00`;
        const [endHours, endMinutes] = timeFormat
          .split(":")
          .map((num) => parseInt(num, 10));
        endDateTime = new Date(endDate);
        endDateTime.setHours(endHours, endMinutes, 0, 0);

        if (
          endDate.toDateString() === startDate.toDateString() &&
          endDateTime <= startDateTime
        ) {
          endDateTime = new Date(startDateTime);
          endDateTime.setMinutes(endDateTime.getMinutes() + 30);
        }
      } else {
        endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        allDay = true;
      }

      allDay =
        !event.startTime || !event.endTime
          ? true
          : endDate.toDateString() !== startDate.toDateString()
          ? false
          : allDay;

      let title = "";
      if (event.applicantName) {
        title = event.applicantName;
      } else {
        let applicantName = null;
        if (event.applicant) {
          applicantName = this.getNameFromObject(event.applicant);
        }

        let employeeName = null;
        if (event.employee) {
          employeeName = this.getNameFromObject(event.employee);
        }

        if (applicantName) {
          title = applicantName;
          if (employeeName) {
            title += ` - ${employeeName}`;
          }
        } else if (employeeName) {
          title = employeeName;
        } else {
          title = event.description || event.title || "Unnamed Event";
        }
      }

      console.log(`Generated title for event ${event.id}: ${title}`);

      return {
        id:
          event.id ||
          `event-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        title: title,
        start: startDateTime,
        end: endDateTime,
        allDay: allDay,
        resource: event.resourceId || null,
        originalEvent: event,
      };
    } catch (error) {
      console.error("Error processing event:", error, event);
      return null;
    }
  };

  getNameFromObject = (obj) => {
    if (!obj) return null;

    if (typeof obj === "string") return null;

    return (
      obj.name ||
      obj.fullName ||
      obj.username ||
      (obj.firstName && obj.lastName
        ? `${obj.firstName} ${obj.lastName}`
        : null)
    );
  };

  processEventsArray = (eventsArray) => {
    if (!Array.isArray(eventsArray)) {
      console.error("Expected array but got:", typeof eventsArray);
      return [];
    }
    return eventsArray
      .map(this.createEventObject)
      .filter((event) => event !== null);
  };

  processEventsObject = (eventsObject) => {
    const formattedEvents = [];
    Object.keys(eventsObject).forEach((dateKey) => {
      const dateEvents = eventsObject[dateKey];
      if (Array.isArray(dateEvents)) {
        dateEvents.forEach((event) => {
          const formattedEvent = this.createEventObject(event);
          if (formattedEvent) formattedEvents.push(formattedEvent);
        });
      }
    });
    return formattedEvents;
  };

  fetchEvents = async () => {
    try {
      const { recruiterId } = this.state;
      if (!recruiterId) {
        console.error("No recruiter ID available for fetching events");
        return;
      }

      console.log("Fetching events for recruiter ID:", recruiterId);

      const recruiterObject = { id: recruiterId };

      const eventsData = await this.presenter.getRecruiterEvents(
        recruiterObject
      );
      console.log("Events data loaded:", eventsData?.length || 0, "events");

      let formattedEvents = [];
      if (Array.isArray(eventsData)) {
        formattedEvents = this.processEventsArray(eventsData);
      } else if (typeof eventsData === "object") {
        formattedEvents = this.processEventsObject(eventsData);
      }

      console.log("Formatted events:", formattedEvents.length, "events");
      this.setState({ events: formattedEvents }, this.debugEvents);
    } catch (error) {
      console.error("Error loading events:", error);
      this.setState({ events: [] });
    }
  };

  eventStyleGetter = (event, start, end, isSelected) => {
    let backgroundColor = "#3174ad";

    if (event.originalEvent && event.originalEvent.applicationType) {
      switch (event.originalEvent.applicationType.toLowerCase()) {
        case "online":
          backgroundColor = "#28a745";
          break;
        case "re-process":
          backgroundColor = "#303F9F";
          break;
        default:
          backgroundColor = "#3174ad";
      }
    }

    const style = {
      backgroundColor: backgroundColor,
      borderRadius: "3px",
      opacity: 0.85,
      color: "white",
      border: "0",
      display: "block",
      fontSize: "0.85em",
      overflow: "hidden",
      textOverflow: "ellipsis",
      padding: "2px 5px",
      margin: "0",
      cursor: "pointer",
      width: "100%",
      position: "absolute",
      boxSizing: "border-box",
    };

    if (this.state.view === "month") {
      style.height = "auto";
      style.position = "relative";
    } else if (this.state.view === "agenda") {
      style.position = "relative";
      style.width = "100%";
      style.margin = "2px 0";
      style.height = "auto";
    }

    return { style };
  };

  handleViewChange = (newView) => this.setState({ view: newView });

  handleEventClick = (event) => {
    if (event.originalEvent && event.originalEvent.applicantFormId) {
      const { applicationType, applicantFormId } = event.originalEvent;

      if (applicationType) {
        switch (applicationType.toLowerCase()) {
          case "online":
            this.navigateTo(
              `/me/recruiter/online-applicant/form/${applicantFormId}`
            );
            break;
          case "re-process":
            this.navigateTo(`/me/recruiter/re-process/form/${applicantFormId}`);
            break;
          default:
            alert("Unknown application type. Cannot navigate.");
        }
      } else {
        alert("No application type available for this event.");
      }
    } else {
      alert("No applicantFormId available for this event.");
    }
  };

  renderLegend = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "15px",
          marginBottom: "15px",
          position: "absolute",
          right: "30px",
          top: "106px",
          background: "#f8f8f8",
          padding: "10px 15px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: "#28a745",
              borderRadius: "4px",
              marginRight: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span style={{ fontSize: "14px", fontWeight: "500" }}>
            Online Applicants
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: "#303F9F",
              borderRadius: "4px",
              marginRight: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M23 4v6h-6M1 20v-6h6"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span style={{ fontSize: "14px", fontWeight: "500" }}>
            Re-Process Applicants
          </span>
        </div>
      </div>
    );
  };

  render() {
    const {
      height = "800px",
      hiddenHours = [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23],
    } = this.props;
    const events = Array.isArray(this.state.events) ? this.state.events : [];

    return (
      <>
        <NavBar />
        <div style={{ padding: "25px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "30px",
              marginTop: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Link to="/hr/recruiter/calendar">
                <div
                  style={{
                    backgroundColor: "#E6E6E6",
                    borderRadius: "50%",
                    padding: "6px 11px",
                    marginRight: "10px",
                  }}
                >
                  <i
                    className="bi bi-arrow-left"
                    style={{
                      fontSize: "20px",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  ></i>
                </div>
              </Link>
              <div>
                <h2
                  style={{
                    color: "#D18237",
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: "bold",
                  }}
                >
                  Recruiter Calendar
                </h2>
                <p style={{ margin: 0, color: "#555", fontSize: "15px" }}>
                  {this.state.recruiterUsername}
                </p>
              </div>
            </div>
            {this.renderLegend()}
          </div>

          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            selectable
            onSelectSlot={this.handleSelect}
            onSelectEvent={this.handleEventClick}
            defaultView="month"
            views={{
              month: true,
              week: true,
              work_week: true,
              day: true,
              agenda: true,
            }}
            onView={this.handleViewChange}
            eventPropGetter={this.eventStyleGetter}
            step={60}
            timeslots={2}
            formats={{
              timeGutterFormat: (date, culture, localizer) =>
                hiddenHours.includes(moment(date).hour())
                  ? ""
                  : localizer.format(date, "h:mm A", culture),
              eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
                localizer.format(start, "h:mm A") +
                " - " +
                localizer.format(end, "h:mm A"),
            }}
            style={{ height }}
            min={new Date(0, 0, 0, 0, 0, 0)}
            max={new Date(0, 0, 0, 23, 59, 59)}
            dayLayoutAlgorithm="no-overlap"
            resizable
            showMultiDayTimes={true}
            slotPropGetter={(date) => ({
              style: {
                backgroundColor:
                  date.getMinutes() === 0
                    ? "rgba(240, 240, 240, 0.2)"
                    : "rgba(248, 248, 248, 0.2)",
              },
            })}
          />
        </div>
      </>
    );
  }
}

export default withRouter(RecruiterCalendar);
